/*
 * Este archivo no debería tener imports a otros types de otras carpetas.
 * Esto es para evitar el error de "import/no-cycle"
 */

import { PaletteMode } from '@mui/material';
import { AxiosError } from 'axios';
import { t } from 'i18next';
import { Action } from 'redux';

import { type MRT_Row as MRTRow, type MRT_ColumnDef as MRTColumnDef } from 'material-react-table';
import React from 'react';

export type ThemePreference = PaletteMode | 'system';

interface PermissionsCountry {
  countryAcronym: string;
  stores: string[];
}

export interface Permission {
  permission: string;
  countries: PermissionsCountry[];
}

export enum AppPermissions {
  CONCILIATIONS_DASHBOARD = 'CONCILIATIONS_DASHBOARD',
  DASHBOARD_MASTER_IMPORT = 'DASHBOARD_MASTER_IMPORT',
  DASHBOARD_FISCAL_IMPORT = 'DASHBOARD_FISCAL_IMPORT',
  CONCILIATIONS_DETAILS = 'CONCILIATIONS_DETAILS',
  CONCILIATIONS_HEADER = 'CONCILIATIONS_HEADER',
  REPROCESS = 'REPROCESS',
  SYNCHRO_4_ME = 'SYNCHRO_4_ME',
  CLOSING_DAYS_REPORT = 'CLOSING_DAYS_REPORT',
  PARAMETRIC_STORES = 'PARAMETRIC_STORES',
}

export interface HttpRequestError {
  notified: boolean;
  code: number;
  data?: object;
  innerException?: AxiosError;
}

export interface RemoteRequest {
  loading: boolean;
  loaded: boolean;
  error: HttpRequestError | null;
  failure: boolean;
}

export interface RemoteResource<TData> extends RemoteRequest {
  data: TData;
}

export interface Division {
  id: string | number;
  label: string;
  divisionId: string;
  divisionName: string;
}

export interface Country extends Division {
  countryId: string;
  countryCode: string;
}

export interface RegionalManagement extends Country {
  regionalMgmt: string | null;
  regionalMgmtId: string | null;
}

export interface Agreement extends Country {
  agreementId: string | number;
  agreementType: string | null;
}

export interface Region extends RegionalManagement {
  region: string | null;
  regionId: string | null;
}

export interface Management extends Region {
  mgmtId: string | null;
  mgmt: string | null;
}

export interface Supervision extends Management {
  supervisionId: string;
  supervision: string | null;
}

export interface Company {
  countries: Country[];
  companyId: string | null | number;
  companyName: string | null;
}

export interface Stores {
  oakStoreId: string;
  countryDescription: string;
  storeId: string;
  costCenter: string;
  createdDate: string;
  brBaseStoreId: string;
  closeDate: string;
  brFranchiseInd: string;
  country: string;
  storeMoreModifyDate: string;
  name: string;
  groupId: string;
  estadoSgl: string;
  openDate: string;
  postalCode: string;
  city: string;
  stateProvince: string;
  cidadeId: string;
  addressLine1: string;
  storeModifyDate: string;
  mailUser: string;
  countryId: string;
  phone: string;
  storeAcronym: string;
  countryCode: string;
  agreementType: string | null;
  agreementId: string | null;
  companyId: string | null | number;
  companyName: string | null;
  regionalMgmt: string | null;
  regionalMgmtId: string | null;
  region: string | null;
  regionId: string | null;
  mgmt: string | null;
  mgmtId: string | null;
  supervision: string | null;
  supervisionId: string | null;
}

export type OpStructures = {
  divisionId: string;
  division: string;
  countryId: string | number;
  country: string;
  regionalMgmtId: string | null;
  regionalMgmt: string | null;
  regionId: string | null;
  region: string | null;
  mgmtId: string | null;
  mgmt: string | null;
  supervisionId: string | null;
  supervision: string | null;
  storeEntityId: string | number;
  storeAcronym: string;
  companyId: number | string | null;
  companyName: string | null;
  agreementId: string | null | number;
  agreementType: string | null;
}[];

export interface PosType {
  segmentId: string;
  segmentDesc: string;
}

export interface MasterData {
  divisions: string[];
  countries: Country[];
  regionalManagements: RegionalManagement[];
  regions: Region[];
  managements: Management[];
  supervisions: Supervision[];
  stores: Stores[];
  posTypes: PosType[];
  companies: Company[];
  agreements: Agreement[];
  federativeEntities: Record<string, string[]>;
}

export interface StoreExtraInfoParameters {
  storeId: number;
  name: string;
  baseStoreId: number | undefined;
}

export interface HttpErrorBaseAction<TAction> extends Action<TAction> {
  error: HttpRequestError;
}

export interface DateValue {
  raw: object | null | undefined;
  accept: Date | null;
}

export enum ConciliationHeaderState {
  CONCILIATION_IN_PROGRESS = 'CONCILIATION IN PROGRESS',
  CONCILIATION_ERROR = 'CONCILIATION ERROR',
  CONCILIATION_OK = 'CONCILIATION OK',
}

export enum ConciliationDetailErrors {
  DIFFERENTS_GCS = 'DIFFERENTS GCS',
  DIFFERENTS_AMOUNTS = 'DIFFERENTS AMOUNTS',
  DIFFERENTS_CNPJ = 'DIFFERENTS CNPJ',
  DIFFERENTS_STATUS = 'DIFFERENTS STATUS',
  EXISTS_IN_FISCAL_AND_NOT_IN_FV = 'EXISTS IN FISCAL AND NOT IN FV',
  EXISTS_IN_FV_AND_NOT_IN_FISCAL = 'EXISTS IN FV AND NOT IN FISCAL',
  POS_NOT_IN_FISCAL = 'POS NOT IN FISCAL',
  POS_NOT_IN_FV = 'POS NOT IN FV',
  DIFFERENTS_POS_AMOUNT = 'DIFFERENTS POS AMOUNT',
  DIFFERENTS_POS_GCS = 'DIFFERENTS POS GCS',
}

export interface ReportLimits {
  headerLimitDays: number;
  detailLimitDays: number;
  synchroLimitDays: number;
}
export interface RuleDefinition {
  id: number;
  code: string;
  description: string;
}

export interface CountriesRulesParameters {
  definitions: RuleDefinition[];
  assignments: {
    [countryId: string]: number[];
  };
  conciliationMethods: {
    [countryId: string]: 'G' | 'D';
  };
}

export interface SynchroParameters {
  enabledCountries: {
    value: string[];
    parameter_name: string;
  };
}

export interface AppParameters {
  filtersLimitMonths: number;
  dashboardTimer: number /** minutes */;
  storeExtraInfo?: StoreExtraInfoParameters[];
  statuses: ConciliationHeaderState[];
  errors: ConciliationDetailErrors[];
  countriesRules: CountriesRulesParameters;
  reportLimits?: ReportLimits | null;
  synchro?: SynchroParameters;
  managementReportFilterLimit?: {
    value: string;
    parameter_name: string;
  };
}

export type ConciliationHeaderId = string;

export type ReportReliefsGroupBy = 'countryAcronym' | 'storeAcronym' | 'businessDate';
export type ReportCancellationsGroupBy = 'countryAcronym' | 'storeAcronym' | 'accountingDate';
export type ReportThirdPartySalesByItemsGroupBy =
  | 'countryAcronym'
  | 'storeAcronym'
  | 'accountingDate'
  | 'plu';

export type PodOptionType = 'MOTHER' | 'CNPJ';

interface BussinessDateFilter {
  startBusinessDate: DateValue;
  endBusinessDate: DateValue;
}

interface OperationDateFilter {
  startOperationDate: DateValue;
  endOperationDate: DateValue;
}

interface AccountingDateFilter {
  startAccountingDate: DateValue;
  endAccountingDate: DateValue;
}

export enum FranchiseType {
  Y = 'Y',
  N = 'N',
}

export interface ConciliationFiltersBase {
  stores: Stores[];
  startDate: DateValue;
  endDate: DateValue;
}

export interface ConciliationDetailFilters extends ConciliationFiltersBase {
  conciliationDetailErrors: ConciliationDetailErrors[];
}

export interface ConciliationHeaderFilters {
  conciliationHeaderId: ConciliationHeaderId;
  conciliationHeaderState: ConciliationHeaderState[];
}

export interface ReportFilter
  extends BussinessDateFilter,
    OperationDateFilter,
    AccountingDateFilter {
  country: Country | null;
  countryCode: string;
  stores: Stores[];
  franchiseTypes: FranchiseType[];
  federativeEntities: string[];
  segments: PosType[];
  pos: string[];
  startDate: DateValue;
  endDate: DateValue;
  thirdPartySalesByItemsGroupBy: ReportThirdPartySalesByItemsGroupBy[];
  differenceType: string;
  pod: PodOptionType[];
  accountingEntriesFields: boolean;
  conciliationDetailFilters: ConciliationDetailFilters;
  conciliationHeaderFilters: ConciliationHeaderFilters;
}

export interface OpStructuresFilter {
  agreement: Agreement[];
  regionalMgmt: RegionalManagement[];
  region: Region[];
  management: Management[];
  supervision: Supervision[];
  company: Company[];
}

export type FilterApplied = (keyof ReportFilter)[];

export type FindConciliationsStatusResponse = {
  country: string;
  start_date: string;
  end_date: string;
  closures_received: number;
  conciliation_ok: string | number;
  number_of_restaurants: number;
};

export interface ConciliationStatus {
  country: string;
  startDate: Date;
  endDate: Date;
  closuresReceived: number;
  conciliationOk: number;
  numberOfRestaurants: number;
}

export const conciliationsStatusKeys: { [K in keyof ConciliationStatus]: K } = {
  country: 'country',
  startDate: 'startDate',
  endDate: 'endDate',
  closuresReceived: 'closuresReceived',
  conciliationOk: 'conciliationOk',
  numberOfRestaurants: 'numberOfRestaurants',
};

export interface DateRange {
  fromDt: string;
  toDt: string;
}

export enum ConciliationsStateTimeLapse {
  yesteday = 'yesterday',
  currentMonth = 'currentMonth',
  lastMonth = 'lastMonth',
}

/*
export type ChildMap<K extends string = string> =
  Readonly<Record<K, ReadonlyArray<Readonly<Entry>>>>;
*/

export type Entry = {
  rowKey: string;
  accessor: string;
  prefix?: string;
  suffix?: string;
  renderCell?: (...args: any[]) => React.ReactNode;
};

export type ChildMap = {
  labelColumnAccessor: string;
  childRows: ReadonlyArray<string>;
  map: Record<string, Entry[]>;
};

export type RowData = Record<string, any>;

export type RenderNestedComponent = (
  row: MRTRow<RowData>,
  columns: MRTColumnDef<RowData, React.ReactNode>[],
  hiddenColumns: Set<string>,
  colSizeMap: Record<string, number>,
  childMap: ChildMap
) => React.ReactNode;

export interface OnClickTo {
  pathname: string;
  paramRows: string[];
}

export type FilterByColumn = {
  accessor: string;
  selector: (state: any) => unknown;
};

export interface ConciliationHeader {
  conciliationHeaderId: string;
  country: string;
  store: string;
  accountingDate: Date;
  status: string;
  gcs: string | number;
  totalAmt: string | number;
  gcsFs: string | number;
  totalAmtFs: string | number;
  gcsOk: string | number;
  totalAmtOk: string | number;
  gcsDif: string | number;
  totalAmtDif: string | number;
  gcsFv: string | number;
  totalAmtFv: string | number;
  gcsFiscal: string | number;
  totalAmtFiscal: string | number;
  porcOk: string | number;
  flexVentas?: string;
  totalAmount?: string | number;
  fiscal?: string | number;
  notConciliated?: string | number;
  registeredOnlyInFlexVentas?: string | number;
  registeredOnlyInFiscal?: string | number;
  cancelledBySubstitution?: string;
  qtyCancSust: number;
  totalAmtCancSust: number;
}

export const conciliationHeaderKeys: { [K in keyof ConciliationHeader]: K } = {
  conciliationHeaderId: 'conciliationHeaderId',
  country: 'country',
  store: 'store',
  accountingDate: 'accountingDate',
  status: 'status',
  gcs: 'gcs',
  totalAmt: 'totalAmt',
  gcsFs: 'gcsFs',
  totalAmtFs: 'totalAmtFs',
  gcsOk: 'gcsOk',
  totalAmtOk: 'totalAmtOk',
  gcsDif: 'gcsDif',
  totalAmtDif: 'totalAmtDif',
  gcsFv: 'gcsFv',
  totalAmtFv: 'totalAmtFv',
  gcsFiscal: 'gcsFiscal',
  totalAmtFiscal: 'totalAmtFiscal',
  porcOk: 'porcOk',
  flexVentas: 'flexVentas',
  fiscal: 'fiscal',
  notConciliated: 'notConciliated',
  registeredOnlyInFlexVentas: 'registeredOnlyInFlexVentas',
  registeredOnlyInFiscal: 'registeredOnlyInFiscal',
  qtyCancSust: 'qtyCancSust',
  totalAmtCancSust: 'totalAmtCancSust',
};

export const concHeaderStateType = {
  CONCILIATION_IN_PROGRESS: 'CONCILIATION IN PROGRESS',
  CONCILIATION_ERROR: 'CONCILIATION ERROR',
  CONCILIATION_OK: 'CONCILIATION OK',
  CONCILIATION_SQUARE_ERROR: 'CONCILIATION SQUARE ERROR',
  CONCILIATION_FAILED: 'CONCILIATION FAILED',
} as const;

export type ConcHeaderStateType = (typeof concHeaderStateType)[keyof typeof concHeaderStateType];

export type ConcHeaderStateTypeKeys = keyof typeof concHeaderStateType;

export type ConcHeaderStateCount = {
  [K in ConcHeaderStateTypeKeys]: number;
};

export const concHeaderStateValueToKey: Record<ConcHeaderStateType, ConcHeaderStateTypeKeys> =
  Object.entries(concHeaderStateType).reduce((acc, [k, v]) => {
    acc[v as ConcHeaderStateType] = k as ConcHeaderStateTypeKeys;
    return acc;
  }, {} as Record<ConcHeaderStateType, ConcHeaderStateTypeKeys>);

export type ConcHeaderFilterByState = string[];

export type ConciliationHeaderMeta = {
  stateCounts: ConcHeaderStateCount;
  filterByState: ConcHeaderFilterByState;
};

export const concDetErrorType = {
  EXISTS_IN_FV_AND_NOT_IN_FISCAL: 'EXISTS IN FV AND NOT IN FISCAL',
  EXISTS_IN_FISCAL_AND_NOT_IN_FV: 'EXISTS IN FISCAL AND NOT IN FV',
  DIFFERENTS_STATUS: 'DIFFERENTS STATUS',
  DIFFERENTS_CNPJ: 'DIFFERENTS CNPJ',
  DIFFERENTS_AMOUNTS: 'DIFFERENTS AMOUNTS',
  DIFFERENTS_POS_AMOUNT: 'DIFFERENTS POS AMOUNT',
  POS_NOT_IN_FV: 'POS NOT IN FV',
  DIFFERENTS_POS_GCS: 'DIFFERENTS POS GCS',
  POS_NOT_IN_FISCAL: 'POS NOT IN FISCAL',
} as const;

export type ConcDetErrorType = (typeof concDetErrorType)[keyof typeof concDetErrorType];

export type ConcDetErrorTypeKeys = keyof typeof concDetErrorType;

export type ConcDetErrorCount = {
  [K in ConcDetErrorTypeKeys]: number;
};

export const concDetErrorValueToKey: Record<ConcDetErrorType, ConcDetErrorTypeKeys> =
  Object.entries(concDetErrorType).reduce((acc, [k, v]) => {
    acc[v as ConcDetErrorType] = k as ConcDetErrorTypeKeys;
    return acc;
  }, {} as Record<ConcDetErrorType, ConcDetErrorTypeKeys>);

export type ConcDetailFilterByError = string[];

export type ConciliationDetailMeta = {
  errorsCounts: ConcDetErrorCount;
  filterByError: ConcDetailFilterByError;
};

export interface ConciliationDetail {
  country: string;
  store: string;
  accountingDate: Date;
  fiscalId: string | number;
  fiscalKey: string | number;
  posNum: string | number;
  error: string | ConcDetErrorType;
  transactionTypeFv: string | number;
  discountOnTotalAmtFv: string | number;
  transactionAmtFv: string | number;
  transactionTypeFs: string | number;
  discountOnTotalAmtFs: string | number;
  transactionAmtFs: string | number;
  xjust: string;
  transactionType?: string | number;
  transactionAmt?: string | number;
  discount?: string | number;
  businessDateFv?: string | number;
  GCs: string | number;
  qtySalesFv: string | number;
  qtySalesFs: string | number;
}

export const conciliationDetailKeys: { [K in keyof ConciliationDetail]: K } = {
  country: 'country',
  store: 'store',
  accountingDate: 'accountingDate',
  fiscalId: 'fiscalId',
  fiscalKey: 'fiscalKey',
  posNum: 'posNum',
  error: 'error',
  transactionTypeFv: 'transactionTypeFv',
  discountOnTotalAmtFv: 'discountOnTotalAmtFv',
  transactionAmtFv: 'transactionAmtFv',
  transactionTypeFs: 'transactionTypeFs',
  discountOnTotalAmtFs: 'discountOnTotalAmtFs',
  transactionAmtFs: 'transactionAmtFs',
  xjust: 'xjust',
  transactionType: 'transactionType',
  transactionAmt: 'transactionAmt',
  discount: 'discount',
  businessDateFv: 'businessDateFv',
  qtySalesFv: 'qtySalesFv',
  qtySalesFs: 'qtySalesFs',
  GCs: 'GCs',
};

export interface ValidationError {
  field?: string;
  code: string;
}

export interface MasterImportExecutionForm {
  mastersTypes?: string;
  countries?: Country[];
  days?: number;
}

export interface DashboardAutoUpdate {
  time: number;
  lastUpdate: Date;
}

export type DashboardRequests =
  | 'SALES'
  | 'COLLECTIONS'
  | 'CLOSED_MONTHS'
  | 'MONTHLY_SALES'
  | 'REGISTERED_STORES'
  | 'DAILY_SALES'
  | 'DELAYED_STORES';

export type GroupBy<TValue> = Record<string, TValue[]>;

export type Translation = typeof t | (() => '');

export interface AppNotification {
  variant: 'error' | 'success' | 'warning' | 'info';
  key: string;
  message: (t: Translation) => string;
  duration?: number;
}

export interface SovosImportStatus {
  importDate: string;
  country: string;
  status: string;
  totalTickets: number;
  amount: number;
}

export const sovosStatusKeys: { [K in keyof SovosImportStatus]: K } = {
  importDate: 'importDate',
  country: 'country',
  status: 'status',
  totalTickets: 'totalTickets',
  amount: 'amount',
};

export interface MastersStatus {
  importDate: Date;
  status: string;
}

export const mastersStatusKeys: { [K in keyof MastersStatus]: K } = {
  importDate: 'importDate',
  status: 'status',
};

export interface MasterStatusDetail {
  masterType: string;
  creationDate: Date | undefined;
  error?: string;
  registerQty: number;
  status: string;
}

export interface MastersImport {
  importDate?: Date;
  type: string;
  country: string;
  description: string;
  quantity: number;
  state: string;
}
export interface MastersImportDetailFilter {
  countries: Country[];
  startImportDate: DateValue;
  endImportDate: DateValue;
  state: string[];
}

export interface FiscalDetail {
  authorizationDate: Date;
  country: string;
  store: string;
  fiscalId: string | number;
  model: string;
  status: string;
  registry_qty: number;
}

export interface FiscalDetailFilter {
  countries: Country[];
  stores: Stores[];
  startDate: DateValue;
  endDate: DateValue;
}

export interface CreateReprocessForm {
  country?: string;
  store?: string;
  businessDateFrom?: Date | null;
  businessDateTo?: Date | null;
  justification: string;
}

export interface Reprocess {
  country: string;
  store: string;
  businessDate: Date;
  type: string;
  justification: string;
  status: string;
  user: string;
  executionDate: Date;
  creationDate: Date;
  federativeEntity: string;
}

export interface ReprocessFilter {
  countryCode: string;
  storeAcronym: string;
  businessDate: DateValue;
  reprocessType: string;
  status: string;
  federativeEntity: string;
}

export interface ReprocessParameters {
  statuses: string[];
  configuration?: {
    max_date_range: number;
  };
}

export enum ReprocessType {
  sales = 'REPROCESS_SALES',
  collections = 'REPROCESS_COLLECTIONS',
  store = 'REPROCESS_STORE',
  economicAccounting = 'REPROCESS_ECONOMIC_ACCOUNTING',
}
export interface DashboardTotalsByCountry {
  country: string;
  flex: { amount: number; gcs: number };
  sovos: { amount: number; gcs: number };
}
export interface CreateSynchroForm {
  country: string;
  stores: Stores[];
  businessDateFrom: Date | null;
  businessDateTo: Date | null;
  justification: string;
}
export type ClosureEvolutionRow = {
  month: string; // "2026-02-01" o "2026-02-01 00:00:00"
  localesOk: number;
  localesErr: number;
  pctOk: number;
  pctNok: number;
};

export type FindClosureEvolutionRequest = {
  countryAcronym: string; // "BR,PA" (CSV)
  storeAcronym?: string; // "S1,S2" (CSV) opcional
  accountingDateRange: string; // "YYYY-MM-DD,YYYY-MM-DD"
};

export type FindClosureEvolutionResponse = ClosureEvolutionRow[];

export type ClosureEvolutionFilters = {
  startDate: Date | null;
  endDate: Date | null;
};
